import { useEffect, useState } from 'react';
import { Checkbox, CircularProgress, FormControl, InputLabel, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { assignRoles, getAppUserRoles, getRoles, removeRoleFromUser } from '../../../../services/role.service';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import { Role } from '../../../../types/Role';
import { AuthProfile } from '../../../../types/AuthProfile';

const useStyles = makeStyles(() => ({
  container_loadingIndicator: {
    float: 'right',
    marginTop: '-65px',
    display: 'flex',
  },
  container_permissions: {
    display: 'flex',
    flexDirection: 'column',
  },
  loadingIndicator_main: {
    marginTop: '10px',
  },
  loadingIndicator_permission: {
    marginTop: '4px',
    marginRight: '15px',
    marginLeft: '5.5px',
  },
  inputlabel_clickable: {
    cursor: 'pointer',
    width: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  inputLabel_permission: {
    height: '30px',
    cursor: 'pointer',
    width: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.03)',
    },
  },
  w_100: {
    width: '100%',
  },
  formControl_search: {
    margin: 1,
    marginLeft: 0,
    width: '350px',
  },
  header_row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    marginBottom: '8px',
  },
}));

export default function UserRoles(props: {
  appUserId: string;
  authProfile: AuthProfile | undefined;
  setAuthProfile: (authProfile: AuthProfile) => void;
}) {
  const classes = useStyles();
  const { notifyMessage } = useNotifyMessage();

  const [roles, setRoles] = useState<Role[]>([]);
  const [assignedRoleIds, setAssignedRoleIds] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingRoleIds, setLoadingRoleIds] = useState<string[]>([]);
  const [showOnlyAssigned, setShowOnlyAssigned] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  /** Load roles + assigned roles */
  useEffect(() => {
    setIsLoading(true);

    Promise.all([getRoles({}), getAppUserRoles(props.appUserId)])
      .then(([allRoles, userRoles]) => {
        if (allRoles.status === 200) {
          const result = allRoles.data?.results ?? allRoles.data ?? [];
          setRoles(result);
        }

        if (userRoles.status === 200) {
          setAssignedRoleIds(userRoles.data.map((x: Role) => x.id));
        }
      })
      .finally(() => setIsLoading(false));
  }, [props.appUserId]);

  const onCheckBoxChange = async (role: Role) => {
    const roleId = role.id;
    const isCurrentlyAssigned = assignedRoleIds.includes(roleId);

    setLoadingRoleIds((prev) => [...prev, roleId]);
    setIsLoading(true);

    try {
      if (isCurrentlyAssigned) {
        const res = await removeRoleFromUser(props.appUserId, roleId);
        if (res.status === 200) {
          notifyMessage('Roles loaded successfully', 'success');

          props.setAuthProfile(res.data);
          setAssignedRoleIds((prev) => prev.filter((id) => id !== roleId));
        }
      } else {
        const res = await assignRoles(props.appUserId, [roleId]);
        if (res.status === 200) {
          notifyMessage('Roles loaded successfully', 'success');

          props.setAuthProfile(res.data);
          setAssignedRoleIds((prev) => [...prev, roleId]);
        }
      }
    } catch (err: any) {
      notifyMessage(err?.toString() || 'Something went wrong', 'error');
    } finally {
      setLoadingRoleIds((prev) => prev.filter((x) => x !== roleId));
      setIsLoading(false);
    }
  };

  return (
    <div>
      {!roles.length && !isLoading && <span className='txt-muted'>No roles found</span>}

      {roles.length > 0 && (
        <div className={classes.header_row}>
          <CircularProgress size={20} sx={{ visibility: isLoading ? 'visible' : 'hidden' }} />

          <InputLabel className={classes.inputlabel_clickable}>
            <Checkbox checked={showOnlyAssigned} onChange={() => setShowOnlyAssigned(!showOnlyAssigned)} />
            Show only assigned roles
          </InputLabel>
        </div>
      )}

      {/* Search */}
      {roles.length > 0 && (
        <FormControl variant='outlined' className={classes.formControl_search}>
          <TextField size='small' placeholder='Search roles' value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
        </FormControl>
      )}

      {/* Role list */}
      <div className={classes.container_permissions}>
        {roles
          .filter((r) => !showOnlyAssigned || assignedRoleIds.includes(r.id))
          .filter((r) => r.name.toLowerCase().includes(searchValue.toLowerCase()))
          .map((role, idx) => {
            const isAssigned = assignedRoleIds.includes(role.id);
            const isLoadingRole = loadingRoleIds.includes(role.id);

            return (
              <InputLabel key={idx} className={classes.inputLabel_permission}>
                {!isLoadingRole && <Checkbox checked={isAssigned} onChange={() => !isLoading && onCheckBoxChange(role)} />}

                {isLoadingRole && <CircularProgress size={20} className={classes.loadingIndicator_permission} />}

                {role.name}
              </InputLabel>
            );
          })}
      </div>
    </div>
  );
}
